import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {Typography, Box, Stack} from '@mui/material';
import {t} from 'i18next';
import FileTypeSelector from '../../../../common/arr-fileTypeSelector/FileTypeSelector';
import {
  GetTranslationComponentResponse,
  FileTypeItem,
} from '../../../../../api/types/production.type';
import {Controller, useFormContext} from 'react-hook-form';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import './TranslationComponent.scss';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {FormMode} from '../../CreateNewProductionUtils';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';
import {useDispatch} from 'react-redux';

interface TranslationComponentProps {
  fileGroupList?: FileTypeItem[];
  translationComponentData:
    | Partial<GetTranslationComponentResponse>
    | undefined;
  isTransalationsSelected: boolean;
  isProductionSet: boolean;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

const TranslationComponent: React.FC<TranslationComponentProps> = ({
  fileGroupList = [],
  translationComponentData,
  mode = FormMode.CREATE,
}) => {
  const isViewMode = mode === FormMode.VIEW;
  const {
    control,
    setValue,
    formState: {isDirty},
  } = useFormContext();
  const dispatch = useDispatch();

  const [previousSelectedIds, setPreviousSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (fileGroupList && fileGroupList.length > 0) {
      const preSelected = fileGroupList
        .filter((item) => item.isFileTypeSelected)
        .map((item) => item.fileTypeID.toString());
      setValue(
        'translationComponentModelData.translationSelectedFileGroups',
        preSelected.join(','),
        {shouldDirty: false} // Mark the form as dirty when setting this value
      );
    }
  }, [fileGroupList, setValue]);

  useEffect(() => {
    if (isDirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [isDirty]);

  const availableFileTypes = useMemo(() => {
    const baseFileTypes = fileGroupList;

    if (translationComponentData?.translationSelectedFileGroups) {
      const selectedIds = new Set(
        translationComponentData.translationSelectedFileGroups
          .split(',')
          .map((id) => id.trim())
      );
      return baseFileTypes.map((fileType) => ({
        ...fileType,
        isFileTypeSelected: selectedIds.has(fileType.fileTypeID.toString()),
      }));
    }

    return baseFileTypes;
  }, [translationComponentData, fileGroupList]);

  const handleFileTypeSelectionChange = useCallback(
    (selectedIds: string[]) => {
      if (isViewMode) return;

      // Only update if the selection has actually changed
      if (JSON.stringify(selectedIds) !== JSON.stringify(previousSelectedIds)) {
        setValue(
          'translationComponentModelData.translationSelectedFileGroups',
          selectedIds.join(','),
          {shouldDirty: true}
        );

        setPreviousSelectedIds(selectedIds);
      }
    },
    [isViewMode, previousSelectedIds, setValue]
  );

  return (
    <Box className="arr-translations-margin-left">
      <Box className="arr-translations-info-box">
        <InfoOutlinedIcon className="arr-translations-info-icon" />
        <Typography
          variant="subtitle1"
          className="text-warning typo-text label2_size"
          textAlign={'start'}
          data-testid={`translation-component-info-text`}>
          {t(
            'productionsAndExports.createNewProduction.productionComponents.translations.infoText'
          )}
        </Typography>
      </Box>
      <Box className="arr-translations-label">
        <Typography
          variant="subtitle1"
          className="text-headings typo-text subhead4_size"
          textAlign={'start'}
          data-testid={`translation-component-label`}>
          {t(
            'productionsAndExports.createNewProduction.productionComponents.translations.label'
          )}
        </Typography>
      </Box>
      <Box className="arr-translations-note">
        <Typography
          variant="subtitle1"
          className="text-body-subtle typo-text body3_size"
          textAlign={'start'}
          data-testid={`translation-component-note`}>
          {t(
            'productionsAndExports.createNewProduction.productionComponents.translations.note'
          )}
        </Typography>
      </Box>
      <Stack direction={'row'} flexWrap={'wrap'} gap={6} alignItems="start">
        <Box className="native-fileType arr-translations-file-selector">
          <FileTypeSelector
            fileGroupList={availableFileTypes}
            onSelectionChange={handleFileTypeSelectionChange}
            disabled={isViewMode}
            checkboxDataTestId="translation-component-file-type-selector"
          />
        </Box>
        <Controller
          name="translationComponentModelData.produceLoadFile"
          control={control}
          render={({field}) => (
            <Box className="arr-translation-produce-load-file native-toggle">
              <ArrToggle
                {...field}
                disabled={isViewMode}
                checked={field.value}
                onChange={(e) => {
                  if (isViewMode) return;
                  field.onChange(e.target.checked);
                }}
                dataTestId="translation-modal-toggle"
              />
              <Typography
                className="toggle-text typo-text subhead5_size"
                data-testid={`translation-component-generate-translation-load-file`}>
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.translations.generateTranslationLoadFile'
                )}
              </Typography>
            </Box>
          )}
        ></Controller>
      </Stack>
    </Box>
  );
};

export default TranslationComponent;
